<template>
  <main class="report-dashboard">
    <header>
      <h1>Кабинет руководителя</h1>
      <div class="report-dashboard__tags">
        <date-range-with-presets
          v-bind:value="period"
          custom-color-date="black"
          v-on:input="onInputDates" />
      </div>
    </header>
    <div class="report-dashboard__diagram">
      <diagram-panel
        v-for="(diagram, index) in diagrams"
        v-bind:key="`diagram-${index}`"
        v-bind:title="diagram.title"
        v-bind:navigation-to-report="{ name: diagram.route }"
        v-bind:options="diagram.options"
        v-bind:type="diagram.type"
        v-bind:items="diagram.items"
        v-bind:plugins="diagram.plugins"
        v-bind:shown-legend="diagram.type === 'doughnut'"
        v-on:input-date="fetchReport($event, diagram.name)"
      />
    </div>
  </main>
</template>

<script>
import DiagramPanel from '@/components/DiagramPanel.vue';
import notifierMixin from '@/mixins/notifierMixin';
import reportSalesMixin from '@/mixins/reportSalesMixin';
import debounce from 'lodash.debounce';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';

/**
 * @description Конфигурация диаграмм
 */
const diagramConfig = {
  totalRevenue: {
    route: 'ClinicPanelReportDirectionsRevenue',
    options: {
      responsive: true,
      cutout: '80%',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (context) => context[0].label,
          label: (context) => ` ${context.formattedValue} ₸`,
        },
      },
    },
  },
  revenueByDirection: {
    route: 'ClinicPanelReportDirectionsRevenue',
    options: {
      responsive: true,
      cutout: '80%',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        animations: {
          width: {
            duration: 0,
          },
        },
        callbacks: {
          title: (context) => {
            const textChunks = context[0].label.split(' ').reduce((acc, word) => {
              const last = acc.length - 1;
              if (last >= 0 && acc[last].length + word.length + 1 <= 25) {
                acc[last] += ` ${word}`;
              } else {
                acc.push(word);
              }
              return acc;
            }, []);
            return textChunks;
          },
          label: (context) => ` ${context.formattedValue} ₸`,
        },
      },
    },
  },
  visits: {
    route: 'ClinicPanelReportPatientsAdministrators',
    options: {
      responsive: true,
      cutout: '80%',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (context) => context[0].label,
          label: (context) => ` ${context.formattedValue}%`,
        },
      },
    },
  },
  revenueByEmployees: {
    route: 'ClinicPanelReportDirectionsRevenue',
    options: {
      responsive: true,
      indexAxis: 'y',
      scales: {
        x: {
          title: {
            display: true,
            text: 'Тыс. тнг',
            padding: { top: 10 },
            font: {
              size: 12,
            },
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.formattedValue} тыс. тнг`,
          bodyAlign: 'center',
        },
      },
    },
  },
  employeeWorkload: {
    route: 'ClinicPanelReportEmployeesWorkload',
    options: {
      responsive: true,
      indexAxis: 'y',
      scales: {
        x: {
          ticks: {
            min: 0,
            max: 100,
            stepSize: 20,
            reverse: false,
            beginAtZero: true,
            callback(tickValue) {
              return `${tickValue}%`;
            },
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.formattedValue}%`,
        },
        bodyAlign: 'center',
      },
    },
  },
};

/**
 * Страница дашборда отчетов для владельца клиники
 */
export default {
  components: {
    DateRangeWithPresets,
    DiagramPanel,
  },
  computed: {
    /**
     * @description Данные запроса
     * @returns {Object}
     */
    viewQuery() {
      return {
        period: this.period,
      };
    },
  },
  created() {
    this.setPeriodByMonth();
  },
  data() {
    return {
      diagrams: [],
    };
  },
  methods: {
    /**
     * @description Обработать выбор периода для расчета.
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.period = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
      } else {
        this.period = value;
      }
    },
    /**
     * @description Получить отчет
     */
    fetchReport() {
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      const onSuccess = (diagrams) => {
        this.diagrams = diagrams.map((diagram) => {
          // eslint-disable-next-line
          let items = diagram.items;
          if (diagram.name === 'revenueByDirection') {
            items = diagram.items.splice(0, 6);
          }
          return {
            name: diagram.name,
            type: diagram.type,
            title: diagram.title,
            route: diagramConfig[diagram.name].route,
            options: diagramConfig[diagram.name].options,
            plugins: diagramConfig[diagram.name].plugins,
            items: items.map((item) => ({
              label: item.group_name,
              value: item.group_value,
              valueTitle: item.group_value_title,
            })),
          };
        });
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
      };
      this.$reportGateway.getReportDashboard(period)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
  },
  mixins: [
    notifierMixin,
    reportSalesMixin,
  ],
  name: 'ClinicPanelReportDashboard',
  watch: {
    viewQuery: debounce(function _() {
      this.fetchReport();
    }, 300),
  },
};
</script>

<style scoped lang="scss">
.report-dashboard {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 31px 20px 48px 23px;
  &__tags {
    display: flex;
    flex-flow: row wrap;
    gap: 10px;
    align-items: center;
    margin: 30px 0 16px 0;
  }
  &__tag {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    overflow: hidden;
    background: #EAEFF3;
    font-weight: 400;
    font-size: 14px;
    color: #192A3E;
    padding: 12px 13px;
    &:hover,
    &.active {
      background: #0489E0;
      color: #FFFFFF;
    }
  }
  &__diagram {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-gap: 20px 23px;
    div:nth-child(1n) {
      grid-column: 1/3;
    }
    div:nth-child(2n) {
      grid-column: 3/5;
    }
    div:nth-child(3n) {
      grid-column: 5/7;
    }
    div:nth-child(4n) {
      grid-column: 1/4;
    }
    div:nth-child(5n) {
      grid-column: 4/7;
    }
  }
}
@media screen and (max-width: 1820px) {
  .report-dashboard {
    &__diagram {
      grid-template-columns: repeat(3, 1fr);
      div:nth-child(1n) {
        grid-column: 1/2;
      }
      div:nth-child(2n) {
        grid-column: 2/3;
      }
      div:nth-child(3n) {
        grid-column: 3/4;
      }
      div:nth-child(4n) {
        grid-column: 1/4;
      }
      div:nth-child(5n) {
        grid-column: 1/4;
      }
    }
  }
}
@media screen and (max-width: 1420px) {
  .report-dashboard {
    &__diagram {
      grid-template-columns: 1fr;
      div:nth-child(1n) {
        grid-column: 1/2;
      }
      div:nth-child(2n) {
        grid-column: 1/2;
      }
      div:nth-child(3n) {
        grid-column: 1/2;
      }
      div:nth-child(4n) {
        grid-column: 1/2;
      }
      div:nth-child(5n) {
        grid-column: 1/2;
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .report-dashboard {
    padding: 31px 10px 48px;
  }
}
</style>
